import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { ordersAPI, itemsAPI } from '@/lib/api';
import { getToday, formatDate } from '@/lib/utils';
import { ShoppingCart, Package, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const LocalDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentOrder, setCurrentOrder] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [stats, setStats] = useState({
    totalItems: 0,
    ordersThisWeek: 0,
    pendingOrder: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const today = getToday();
      
      try {
        const orderRes = await ordersAPI.getCurrent(today);
        setCurrentOrder(orderRes.data);
        setStats(prev => ({ ...prev, pendingOrder: orderRes.data.status === 'draft' }));
      } catch (err) {
        setCurrentOrder(null);
      }

      const itemsRes = await itemsAPI.getAll(true);
      setStats(prev => ({ ...prev, totalItems: itemsRes.data.length }));

      const historyRes = await ordersAPI.getHistory(7);
      setStats(prev => ({ ...prev, ordersThisWeek: historyRes.data.length }));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-4 space-y-6"
      data-testid="local-dashboard"
    >
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">{formatDate(new Date())}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.totalItems}</p>
              <p className="text-xs text-muted-foreground">Productos</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.ordersThisWeek}</p>
              <p className="text-xs text-muted-foreground">Pedidos (7d)</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Pedido de hoy</h3>
        </div>
        <div className="p-4">
          {currentOrder ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {currentOrder.status === 'submitted' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  )}
                  <span className="text-sm font-medium">
                    {currentOrder.status === 'submitted' ? 'Enviado' : 'Borrador'}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {currentOrder.items.length} productos
                </span>
              </div>
              <Button
                onClick={() => navigate('/local/order')}
                className="w-full"
                variant={currentOrder.status === 'draft' ? 'default' : 'outline'}
                data-testid="view-order-button"
              >
                {currentOrder.status === 'draft' ? 'Completar pedido' : 'Ver pedido'}
              </Button>
            </div>
          ) : (
            <div className="text-center py-6">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">No hay pedido para hoy</p>
              <Button
                onClick={() => navigate('/local/order')}
                className="w-full"
                data-testid="create-order-button"
              >
                Crear pedido
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary to-accent text-white rounded-lg p-6 shadow-lg">
        <h3 className="font-heading font-bold text-lg mb-2">Acceso rápido</h3>
        <p className="text-sm opacity-90 mb-4">Gestiona tu pedido diario de forma simple y rápida</p>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="secondary"
            className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30"
            onClick={() => navigate('/local/order')}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Pedido
          </Button>
          <Button
            variant="secondary"
            className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30"
            onClick={() => navigate('/local/history')}
          >
            <Package className="w-4 h-4 mr-2" />
            Historial
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default LocalDashboard;